import React from 'react';
import { useNavigate } from 'react-router-dom';
import './TermsPage.css';

function TermsPage() {
  const navigate = useNavigate();

  return (
    <div className="terms-container">
      <div className="terms-header">
        <button className="terms-back-btn" onClick={() => navigate(-1)}>
          ← Back
        </button>
        <h1>Terms of Service & Privacy Policy</h1>
        <p>Last updated: November 2026</p>
      </div>

      <div className="terms-content">

        {/* TERMS OF SERVICE */}
        <section className="terms-section">
          <h2>Terms of Service</h2>

          <div className="terms-block">
            <h3>1. Acceptance of Terms</h3>
            <p>By creating an account or using Couple Check, you agree to these Terms of Service. If you do not agree, please do not use the app. These terms apply to all users of Couple Check, including free and premium subscribers.</p>
          </div>

          <div className="terms-block">
            <h3>2. About Couple Check</h3>
            <p>Couple Check is a private relationship journaling application that allows couples to record, track, and reflect on their relationship experiences. The service is operated in Kenya and is available to users aged 18 and above.</p>
          </div>

          <div className="terms-block">
            <h3>3. User Accounts</h3>
            <p>You are responsible for maintaining the confidentiality of your account credentials including your username, password, and PIN. You are responsible for all activity that occurs under your account. Couple Check is not liable for any loss resulting from unauthorized use of your account.</p>
          </div>

          <div className="terms-block">
            <h3>4. Acceptable Use</h3>
            <p>You agree to use Couple Check only for lawful personal purposes. You must not use the service to store content that is illegal, harmful, abusive, or violates any third-party rights. Couple Check reserves the right to suspend or permanently delete accounts that violate these terms.</p>
            <p>If your account is suspended, you will not be able to log in. The login screen will show the reason for the suspension (where one was recorded) and how to contact us at couplecheck254@gmail.com. Where possible, we also send an in-app notification before a suspension takes effect.</p>
          </div>

          <div className="terms-block">
            <h3>5. Subscriptions and Payments</h3>
            <p>Couple Check offers a Free plan and a Premium plan at KES 249 per month. Premium subscriptions are billed monthly via M-Pesa. All payments are processed through Safaricom's Daraja API. Subscriptions are valid for 30 days from the date of payment. We do not offer automatic renewals — you choose to renew each month. Payments are non-refundable once processed.</p>
          </div>

          <div className="terms-block">
            <h3>6. Plan Features and Limitations</h3>
            <p>Free plan users are limited to 8 journal entries per month for each partner, 3 weekly reports and 1 monthly report per month, and do not have access to private entries, couple's joint reports, or AI-powered feedback. Premium plan users receive unlimited access to all features for the duration of their active subscription. Couple Check reserves the right to modify plan features with reasonable notice.</p>
          </div>

          <div className="terms-block">
            <h3>7. Intellectual Property</h3>
            <p>All content, design, and code within Couple Check is the property of Couple Check. You retain ownership of the journal entries and content you create within the app. You grant Couple Check a limited license to store and process your content solely for the purpose of providing the service.</p>
          </div>

          <div className="terms-block">
            <h3>8. Termination</h3>
            <p>You may delete your account at any time from the Profile page by choosing "Delete My Account" and confirming your username. Deletion is permanent and immediate: your journal entries, profile, subscription records, and notifications are deleted and cannot be recovered.</p>
            <p>Couple Check may suspend or permanently delete accounts that violate these terms. If an account is deleted by an administrator, the same permanent deletion applies.</p>
          </div>

          <div className="terms-block">
            <h3>9. Limitation of Liability</h3>
            <p>Couple Check is provided on an "as is" basis. We do not guarantee uninterrupted or error-free service. To the maximum extent permitted by Kenyan law, Couple Check shall not be liable for any indirect, incidental, or consequential damages arising from your use of the service.</p>
          </div>

          <div className="terms-block">
            <h3>10. Changes to Terms</h3>
            <p>We may update these Terms of Service from time to time. Continued use of Couple Check after changes constitutes acceptance of the new terms. We will notify users of significant changes via in-app notification.</p>
          </div>
        </section>

        <div className="terms-divider" />

        {/* PRIVACY POLICY */}
        <section className="terms-section">
          <h2>Privacy Policy</h2>

          <div className="terms-block">
            <h3>1. Information We Collect</h3>
            <p>We collect the following information when you use Couple Check:</p>
            <ul>
              <li><strong>Account information</strong> — your username, your encrypted password, and, if you set one, your account-recovery security question and its encrypted answer</li>
              <li><strong>Profile information</strong> — partner names, ages, dates of birth, an optional profile picture, and the relationship description you provide</li>
              <li><strong>Journal entries</strong> — the content, ratings, categories, and dates of entries you create</li>
              <li><strong>Payment information</strong> — your M-Pesa phone number and M-Pesa transaction reference number. We do not store your M-Pesa PIN or full payment card details.</li>
              <li><strong>Usage data</strong> — report generation history for enforcing plan limits</li>
            </ul>
          </div>

          <div className="terms-block">
            <h3>2. How We Use Your Information</h3>
            <p>We use your information to:</p>
            <ul>
              <li>Provide and operate the Couple Check service</li>
              <li>Process your subscription payments via M-Pesa</li>
              <li>Generate relationship reports and insights from your journal entries</li>
              <li>Send you in-app notifications such as journaling reminders, subscription renewal alerts, and account notices</li>
              <li>Enforce plan usage limits (entry count, report count)</li>
              <li>Administer accounts and provide support, including reviewing account and subscription details and suspending or deleting accounts that violate our terms</li>
              <li>Respond to your support requests</li>
            </ul>
          </div>

          <div className="terms-block">
            <h3>3. Third-Party Services</h3>
            <p>Couple Check uses the following third-party services that may process your data:</p>
            <ul>
              <li><strong>Safaricom Daraja API</strong> — processes your M-Pesa payment. Your phone number is shared with Safaricom to initiate the payment request. Safaricom's own privacy policy applies to this data.</li>
              <li><strong>Google Gemini AI (report feedback)</strong> — when you request AI-powered feedback on your reports (a Premium feature), only numerical data is sent to Google's Gemini API to generate the feedback: your scores, category names, entry dates, entry counts, and score trends, together with the partner names shown on the report. The text of your journal entries is never sent, and private entries are never included. We do not send your username, phone number, or other contact details.</li>
              <li><strong>Google Gemini AI (profile summaries)</strong> — when a short description of your couple is generated for your profile, including when you use "Generate AI Description" (a Premium feature), your partner names and ages and the relationship description you wrote in your profile may be sent to Google's Gemini API.</li>
            </ul>
            <p>Google's privacy policy applies to the processing it carries out. We do not sell your personal data to any third party.</p>
          </div>

          <div className="terms-block">
            <h3>4. Data Storage and Security</h3>
            <p>Your data is stored on secure servers. Passwords and PINs are encrypted using BCrypt hashing and are never stored in plain text. We implement reasonable technical and organizational measures to protect your data against unauthorized access, alteration, or deletion.</p>
          </div>

          <div className="terms-block">
            <h3>5. Private Entries</h3>
            <p>Journal entries you mark as private are stored with a privacy flag that restricts access. Private entries are only accessible after PIN verification and are never included in AI-powered feedback. Even private entries are stored on our servers in the same way as regular entries — the privacy flag controls visibility within the app, not encryption at the storage level.</p>
          </div>

          <div className="terms-block">
            <h3>6. Data Retention</h3>
            <p>We retain your data for as long as your account is active. When you delete your account from the Profile page, your journal entries, profile, subscription and payment records, and notifications are permanently deleted from our database immediately. The same applies if an administrator deletes your account. Transaction records held by Safaricom M-Pesa are kept under Safaricom's own retention rules.</p>
          </div>

          <div className="terms-block">
            <h3>7. Your Rights</h3>
            <p>You have the right to access, correct, or delete your personal data at any time. You can edit your profile and entries directly within the app, and you can delete your account yourself from the Profile page. For a copy of your data or any other request, contact us at couplecheck254@gmail.com.</p>
          </div>

          <div className="terms-block">
            <h3>8. Children's Privacy</h3>
            <p>Couple Check is not intended for users under the age of 18. We do not knowingly collect personal information from minors. If you believe a minor has created an account, please contact us and we will delete it promptly.</p>
          </div>

          <div className="terms-block">
            <h3>9. Contact for Privacy Matters</h3>
            <p>For any questions or concerns about this Privacy Policy or how we handle your data, contact us at <a href="mailto:couplecheck254@gmail.com" className="terms-link">couplecheck254@gmail.com</a>.</p>
          </div>

          <div className="terms-block">
            <h3>10. Governing Law</h3>
            <p>These Terms of Service and Privacy Policy are governed by the laws of Kenya, including the Data Protection Act 2019.</p>
          </div>
        </section>

        <div className="terms-footer">
          <p>For questions about these terms, contact us at <a href="mailto:couplecheck254@gmail.com" className="terms-link">couplecheck254@gmail.com</a></p>
          <button className="terms-back-btn-bottom" onClick={() => navigate(-1)}>
            ← Back to App
          </button>
        </div>

      </div>
    </div>
  );
}

export default TermsPage;